import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../dates.ts';
import {
  useContact, useContactHistory, useEditContact, useEditLink, useEditReasons, useEndLink, useLinkHistory,
  type Contact, type ContactLink, type EditReason, type HistoryEntry, type LinkFields,
} from '../../api/contacts.ts';
import { LoadFailure } from '../LoadFailure.tsx';
import { ReasonFields, reasonReady } from './ReasonFields.tsx';
import { Flags } from './ContactLinkList.tsx';
import { Sheet, blank, field, hint, label, primary, secondary } from './Sheet.tsx';

/**
 * One contact, opened from a list: their details, every link of theirs the reader can see, and, for the
 * link the list was opened from, Edit and End. Every change asks why (the four reasons); History shows
 * every version, what changed, who and when. A person the reader cannot see is "No such contact": the
 * server never says that they exist.
 */
export function ContactSheet({ contactId, linkId, onClose }: { contactId: string | null; linkId: string; onClose: () => void }) {
  const q = useContact(contactId);
  const [mode, setMode] = useState<'view' | 'edit' | 'link' | 'end'>('view');
  const [history, setHistory] = useState(false);
  const d = q.data;
  const focused = d?.links.find((l) => l.link_id === linkId) ?? null;
  return (
    <Sheet labelId="contact-sheet-title" onClose={onClose}
           title={d ? d.contact.full_name : contactId ? 'Contact' : 'A company on record'}>
      {contactId === null ? (
        <p className="text-neutral-700">This line names a company with no person recorded. Edit it from the company’s page when that comes.</p>
      ) : q.error && !d ? (
        <LoadFailure what="The contact" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} forbiddenIsSession={false} />
      ) : !d ? (
        <p className="py-6 text-center text-neutral-600">Loading…</p>
      ) : mode === 'edit' ? (
        <PersonForm c={d.contact} done={() => setMode('view')} />
      ) : mode === 'link' && focused ? (
        <LinkForm l={focused} done={() => setMode('view')} />
      ) : mode === 'end' && focused ? (
        <EndForm l={focused} done={() => setMode('view')} />
      ) : (
        <>
          {d.contact.merged_into && (
            <p className="mb-2 rounded border border-warning-edge bg-warning-bg px-3 py-2 text-warning-fg">
              Merged into another record of the same person. Its links are that record’s now.
            </p>
          )}
          <Details c={d.contact} />
          <div className="mt-3 flex flex-wrap gap-2">
            {d.may_edit && <button type="button" className={secondary} onClick={() => setMode('edit')}>Edit details</button>}
            {focused?.is_current && (
              <>
                <button type="button" className={secondary} onClick={() => setMode('link')}>Edit {focused.role_label.toLowerCase()} link</button>
                <button type="button" className={secondary} onClick={() => setMode('end')}>End link</button>
              </>
            )}
            <button type="button" className={secondary} onClick={() => setHistory(!history)} aria-expanded={history}>
              {history ? 'Hide history' : d.contact.edits > 0 ? `History (edited ${d.contact.edits} ${d.contact.edits === 1 ? 'time' : 'times'})` : 'History'}
            </button>
            {d.may_merge && <Link to="/contacts/duplicates" className={`${secondary} inline-flex items-center`}>Possible duplicates</Link>}
          </div>
          {history && <Histories contactId={d.contact.id} linkId={focused?.link_id ?? null} />}
          <h3 className="mt-4 text-sm font-bold tracking-[0.08em] text-neutral-600 uppercase">On record as</h3>
          <ul className="mt-1 divide-y divide-line">
            {d.links.map((l) => <Where key={l.link_id} l={l} focused={l.link_id === linkId} />)}
          </ul>
        </>
      )}
    </Sheet>
  );
}

function Details({ c }: { c: Contact }) {
  const address = [[c.street_address, c.unit].filter(Boolean).join(' '), c.city, [c.state, c.zip].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
  const rows: [string, React.ReactNode][] = [
    ['Organization', c.organization],
    ['Job title', [c.job_title, c.credentials].filter(Boolean).join(', ') || null],
    ['Phone', c.phone_primary && <a href={`tel:${c.phone_primary}`} className="inline-flex min-h-[44px] items-center underline">{c.phone_primary}</a>],
    ['Other phone', c.phone_secondary && <a href={`tel:${c.phone_secondary}`} className="inline-flex min-h-[44px] items-center underline">{c.phone_secondary}</a>],
    ['Email', c.email && <a href={`mailto:${c.email}`} className="inline-flex min-h-[44px] items-center underline">{c.email}</a>],
    ['Other email', c.email_secondary && <a href={`mailto:${c.email_secondary}`} className="inline-flex min-h-[44px] items-center underline">{c.email_secondary}</a>],
    ['Address', address || null],
    ['Language', c.language],
    ['Prefers', c.preferred_contact],
  ];
  return (
    <>
      {c.do_not_contact && (
        <p className="mb-2 rounded border border-warning-edge bg-warning-bg px-3 py-2 font-semibold text-warning-fg">
          Do not contact. The reason is on the link it applies to.
        </p>
      )}
      <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
        {rows.filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-neutral-600">{k}</dt>
            <dd className="text-neutral-900">{v}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}

function Where({ l, focused }: { l: ContactLink; focused: boolean }) {
  const place = l.client_id
    ? <Link to={`/clients/${l.client_id}`} className="link inline-flex min-h-[44px] items-center">{l.client_name} · {l.client_ref}</Link>
    : l.facility_id
      ? <Link to={`/facilities/${l.facility_id}`} className="link inline-flex min-h-[44px] items-center">{l.facility_name}</Link>
      : l.property_street ?? 'A property';
  return (
    <li className={`py-2 text-sm ${focused ? 'border-l-4 border-brand pl-2' : ''}`}>
      <p className="text-neutral-900">
        <span className="font-semibold">{l.relationship ? `${l.relationship} · ${l.role_label}` : l.role_label}</span>
        <Flags l={l} />
      </p>
      <p className="text-neutral-700">{place}</p>
      <p className="text-neutral-600">
        {l.is_current ? `Since ${l.started_on ? formatDate(l.started_on) : 'a date not recorded'}` : `Ended ${l.ended_on ? formatDate(l.ended_on) : ''}`}
        {l.account_ref ? ` · Account ${l.account_ref}` : ''}
      </p>
      {l.authority_note && <p className="text-neutral-700">Authority: {l.authority_note}</p>}
      {l.do_not_contact_note && <p className="text-warning-fg">Do not contact: {l.do_not_contact_note}</p>}
      {l.notes && <p className="whitespace-pre-line text-neutral-700">{l.notes}</p>}
    </li>
  );
}

const LABELS: Record<string, string> = {
  organization_id: 'Organization', last_name: 'Surname', first_name: 'Given name', job_title: 'Job title',
  credentials: 'Credentials', phone_primary: 'Phone', phone_secondary: 'Other phone', email: 'Email',
  email_secondary: 'Other email', preferred_contact: 'Prefers', language: 'Language', is_active: 'Active',
  do_not_contact: 'Do not contact', street_address: 'Street', unit: 'Unit', city: 'City', state: 'State', zip: 'Zip',
  contact_id: 'Person', relationship: 'Relationship', is_primary: 'Primary', is_emergency: 'Emergency',
  has_authority: 'Has authority', authority_note: 'Authority note', do_not_contact_note: 'Do-not-contact reason',
  notes: 'Notes', started_on: 'Started', appointed_date: 'Appointed', ended_on: 'Ended', is_current: 'Current',
  court_appointed: 'Court appointed', account_ref: 'Account',
};

function Histories({ contactId, linkId }: { contactId: string; linkId: string | null }) {
  const person = useContactHistory(contactId, true);
  const link = useLinkHistory(linkId, linkId !== null);
  return (
    <div className="mt-3 grid gap-3">
      <HistoryList title="The person" q={person} />
      {linkId && <HistoryList title="This link" q={link} />}
    </div>
  );
}

function HistoryList({ title, q }: { title: string; q: { data?: { history: HistoryEntry[] }; error: Error | null } }) {
  const rows = q.data?.history ?? [];
  return (
    <section>
      <h3 className="text-sm font-bold tracking-[0.08em] text-neutral-600 uppercase">{title}</h3>
      {q.error ? <p className="text-sm text-neutral-700">The history could not be loaded.</p>
        : !q.data ? <p className="text-sm text-neutral-600">Loading…</p>
          : rows.length === 0 ? <p className="text-sm text-neutral-600">Never edited.</p> : (
            <ol className="mt-1 divide-y divide-line text-sm">
              {rows.map((h) => (
                <li key={h.version} className="py-1.5">
                  <p className="text-neutral-900">
                    {h.as_first_recorded ? 'As first recorded' : <><span className="font-semibold">{h.reason}</span>{h.reason_text ? `: ${h.reason_text}` : ''}</>}
                  </p>
                  <p className="text-neutral-600">
                    {formatDate(h.recorded_at.slice(0, 10))}{h.recorded_by ? ` · ${h.recorded_by}` : ''}
                    {h.changed.length > 0 && ` · changed ${h.changed.map((k) => LABELS[k] ?? k).join(', ')}`}
                  </p>
                </li>
              ))}
            </ol>
          )}
    </section>
  );
}

function Problem({ error }: { error: Error | null }) {
  if (!error) return null;
  return <p role="alert" className="mt-2 rounded border border-warning-edge bg-warning-bg px-3 py-2 text-warning-fg">{error.message}</p>;
}

function PersonForm({ c, done }: { c: Contact; done: () => void }) {
  const reasons = useEditReasons();
  const edit = useEditContact(c.id);
  const start = {
    last_name: c.last_name ?? '', first_name: c.first_name ?? '', job_title: c.job_title ?? '',
    phone_primary: c.phone_primary ?? '', phone_secondary: c.phone_secondary ?? '',
    email: c.email ?? '', email_secondary: c.email_secondary ?? '',
    street_address: c.street_address ?? '', unit: c.unit ?? '', city: c.city ?? '', state: c.state ?? '', zip: c.zip ?? '',
    language: c.language ?? '',
  };
  const [v, setV] = useState(start);
  const [dnc, setDnc] = useState(c.do_not_contact);
  const [reason, setReason] = useState<EditReason | null>(null);
  const [text, setText] = useState('');
  const changed = Object.fromEntries(
    (Object.keys(v) as (keyof typeof v)[]).filter((k) => v[k].trim() !== start[k]).map((k) => [k, blank(v[k])]),
  );
  if (dnc !== c.do_not_contact) changed.do_not_contact = dnc as unknown as string;
  const any = Object.keys(changed).length > 0;
  const input = (k: keyof typeof v, title: string, type = 'text') => (
    <div>
      <label htmlFor={`pf-${k}`} className={label}>{title}</label>
      <input id={`pf-${k}`} type={type} className={field} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
    </div>
  );
  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      edit.mutate({ fields: changed, version: c.version, reason: reason!, reason_text: blank(text) }, { onSuccess: done });
    }}>
      <div className="grid gap-2 sm:grid-cols-2">
        {input('last_name', 'Surname')}
        {input('first_name', 'Given name')}
        {input('job_title', 'Job title')}
        {input('language', 'Language')}
        {input('phone_primary', 'Phone', 'tel')}
        {input('phone_secondary', 'Other phone', 'tel')}
        {input('email', 'Email', 'email')}
        {input('email_secondary', 'Other email', 'email')}
        {input('street_address', 'Street')}
        {input('unit', 'Unit')}
        {input('city', 'City')}
        {input('state', 'State')}
        {input('zip', 'Zip')}
      </div>
      <label className="mt-3 inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-neutral-800">
        <input type="checkbox" checked={dnc} onChange={(e) => setDnc(e.target.checked)} className="h-5 w-5" />
        Do not contact this person (say why on the link it applies to)
      </label>
      <ReasonFields id="pf" reason={reason} text={text} onReason={setReason} onText={setText} />
      <Problem error={edit.error} />
      <div className="mt-3 flex gap-2">
        <button type="submit" className={primary} disabled={!any || !reasonReady(reasons, reason, text) || edit.isPending}>
          {edit.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className={secondary} onClick={done}>Cancel</button>
      </div>
      {!any && <p className={hint}>Change something to save it.</p>}
    </form>
  );
}

function LinkForm({ l, done }: { l: ContactLink; done: () => void }) {
  const reasons = useEditReasons();
  const edit = useEditLink(l.link_id);
  const accountWhole = !(l.account_ref ?? '').startsWith('••••');
  const [relationship, setRelationship] = useState(l.relationship ?? '');
  const [flags, setFlags] = useState({ is_primary: l.is_primary, is_emergency: l.is_emergency, has_authority: l.has_authority, court_appointed: l.court_appointed });
  const [authority, setAuthority] = useState(l.authority_note ?? '');
  const [dncNote, setDncNote] = useState(l.do_not_contact_note ?? '');
  const [account, setAccount] = useState(accountWhole ? l.account_ref ?? '' : '');
  const [started, setStarted] = useState(l.started_on ?? '');
  const [reason, setReason] = useState<EditReason | null>(null);
  const [text, setText] = useState('');
  const fields: LinkFields = {};
  if (relationship.trim() !== (l.relationship ?? '')) fields.relationship = blank(relationship);
  for (const k of Object.keys(flags) as (keyof typeof flags)[]) if (flags[k] !== l[k]) fields[k] = flags[k];
  if (authority.trim() !== (l.authority_note ?? '')) fields.authority_note = blank(authority);
  if (dncNote.trim() !== (l.do_not_contact_note ?? '')) fields.do_not_contact_note = blank(dncNote);
  if (accountWhole && account.trim() !== (l.account_ref ?? '')) fields.account_ref = blank(account);
  if (started !== (l.started_on ?? '')) fields.started_on = started || null;
  const any = Object.keys(fields).length > 0;
  const box = (k: keyof typeof flags, title: string) => (
    <label className="inline-flex min-h-[44px] items-center gap-2 text-sm text-neutral-800">
      <input type="checkbox" checked={flags[k]} onChange={(e) => setFlags({ ...flags, [k]: e.target.checked })} className="h-5 w-5" />
      {title}
    </label>
  );
  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      edit.mutate({ fields, version: l.version, reason: reason!, reason_text: blank(text) }, { onSuccess: done });
    }}>
      <p className="text-sm text-neutral-700">{l.role_label}{l.client_name ? ` · ${l.client_name}` : ''}</p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <div>
          <label htmlFor="lf-rel" className={label}>Relationship</label>
          <input id="lf-rel" className={field} value={relationship} onChange={(e) => setRelationship(e.target.value)} maxLength={48} />
        </div>
        <div>
          <label htmlFor="lf-start" className={label}>Since</label>
          <input id="lf-start" type="date" className={field} value={started} onChange={(e) => setStarted(e.target.value)} />
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4">
        {box('is_primary', 'Primary')}
        {box('is_emergency', 'Emergency contact')}
        {box('has_authority', 'Has authority')}
        {box('court_appointed', 'Court appointed')}
      </div>
      {flags.has_authority && (
        <div className="mt-2">
          <label htmlFor="lf-auth" className={label}>What authority</label>
          <input id="lf-auth" className={field} value={authority} onChange={(e) => setAuthority(e.target.value)} />
        </div>
      )}
      <div className="mt-2">
        <label htmlFor="lf-dnc" className={label}>Do-not-contact reason, for this link</label>
        <input id="lf-dnc" className={field} value={dncNote} onChange={(e) => setDncNote(e.target.value)} />
      </div>
      {accountWhole && (l.role_code === 'PROP_UTILITY' || l.account_ref) && (
        <div className="mt-2">
          <label htmlFor="lf-acct" className={label}>Account</label>
          <input id="lf-acct" className={field} value={account} onChange={(e) => setAccount(e.target.value)} maxLength={64} />
        </div>
      )}
      <ReasonFields id="lf" reason={reason} text={text} onReason={setReason} onText={setText} />
      <Problem error={edit.error} />
      <div className="mt-3 flex gap-2">
        <button type="submit" className={primary} disabled={!any || !reasonReady(reasons, reason, text) || edit.isPending}>
          {edit.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className={secondary} onClick={done}>Cancel</button>
      </div>
    </form>
  );
}

function EndForm({ l, done }: { l: ContactLink; done: () => void }) {
  const reasons = useEditReasons();
  const end = useEndLink(l.link_id);
  const [on, setOn] = useState('');
  const [reason, setReason] = useState<EditReason | null>('UPDATED_INFORMATION');
  const [text, setText] = useState('');
  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      end.mutate({ ended_on: on || null, reason: reason!, reason_text: blank(text) }, { onSuccess: done });
    }}>
      <p className="text-neutral-800">
        {l.name ?? 'This person'} will no longer be listed as {l.role_label.toLowerCase()}. The person and the history stay.
      </p>
      <div className="mt-2">
        <label htmlFor="ef-on" className={label}>Ended on</label>
        <input id="ef-on" type="date" className={field} value={on} onChange={(e) => setOn(e.target.value)} />
        <p className={hint}>Leave empty for today. Correction means it was recorded in error.</p>
      </div>
      <ReasonFields id="ef" reason={reason} text={text} onReason={setReason} onText={setText} />
      <Problem error={end.error} />
      <div className="mt-3 flex gap-2">
        <button type="submit" className={primary} disabled={!reasonReady(reasons, reason, text) || end.isPending}>
          {end.isPending ? 'Ending…' : 'End link'}
        </button>
        <button type="button" className={secondary} onClick={done}>Cancel</button>
      </div>
    </form>
  );
}
